/**
 * @flow
 */

// eslint-disable-next-line max-classes-per-file
import "whatwg-fetch";
import includes from "lodash/includes";
import range from "lodash/range";
import { InternalOnlyError } from "./utils/errors";
import { logBreadcrumbToSentry, logErrorBreadcrumbToSentryAndRethrow, sentryTrackedFetch } from "./utils/sentryUtils";
import { FETCH_TIMEOUT_MS, HTTP_202_ACCEPTED, HTTP_204_NO_CONTENT, HTTP_401_UNAUTHORIZED } from "./const/apiConstants";

export type ResponseNoDataSuccess = {|
  statusCode: number,
  responseType: "SUCCESS_NO_DATA",
|};

export type ResponseDataSuccess<SuccessType> = {|
  statusCode: number,
  responseType: "SUCCESS",
  data: SuccessType,
|};

export const NonFieldErrors = "non_field_errors" as "non_field_errors";
export type FieldError = {
  id?: number,
  code?: number,
  message: string,
  field?: string,
};

export type NonFieldError = {
  id?: number,
  code?: number,
  field: typeof NonFieldErrors,
  message: {
    __all__: string,
  },
};

export type ResponseErrorType = {
  code: number,
  message: string,
  errors?: Array<NonFieldError | FieldError>,
  remediation?: string,
};

export const innerErrorMessageFromError = (innerError: NonFieldError | FieldError): string => {
  if (typeof innerError.message === "object") {
    return `${innerError.message.__all__}`;
  }
  return innerError.message;
};

const formatAsPrefix = (message: string): string => {
  return message.trim().replace(/\.$/, "");
};

export const getDescriptiveErrorMessage = (error: ResponseErrorType): string => {
  const prefix = formatAsPrefix(error.message);
  // Some errors, like non_field_errors, have a very unuseful message (like "ValidationError") and the actual useful
  // error message has to be taken from the errors themselves.
  if (error.errors != null && error.errors.length > 0) {
    const innerError: FieldError | NonFieldError = error.errors[0];
    const innerErrorMessage = innerErrorMessageFromError(innerError);
    const { remediation } = error;
    if (innerErrorMessage && remediation != null) {
      const innerPrefix = formatAsPrefix(innerErrorMessage);
      // eslint-disable-next-line i18next/no-literal-string
      return `${prefix}: ${innerPrefix}. ${remediation}`;
    }
    if (innerErrorMessage) {
      return `${prefix}: ${innerErrorMessage}`;
    }
  }
  if (error.remediation != null) {
    // eslint-disable-next-line i18next/no-literal-string
    return `${prefix}. ${error.remediation}`;
  }
  return error.message;
};

export class ResponseError {
  // The numerical code identifying this error. This is typically an HTTP code like 400 or 500.
  code: number;

  // The basic high-level messaging for this error, e.g. "Something went wrong" or "Failed to update ABC WFM".
  message: string;

  // Full-detail messaging on the error. This messaging is likely long and detailed, so we should limit its
  // display to suitable UI and users. If, for example, the UI space for the error is not large enough to
  // accommodate this or a regular employee wouldn't find a lot of detail to be valuable, then we may choose
  // to show `employeeMessage` instead.
  descriptiveErrorMessage: string;

  // Detailed error breakdown. e.g. Multiple fields may be invalid, causing an overall form validation error.
  errors: ?Array<FieldError | NonFieldError>;

  // Messaging (when available) that advises the user on how to address the issue.
  remediation: ?string;

  // Error messaging that's suitable for regular employees.
  employeeMessage: string;

  constructor(error: ResponseErrorType) {
    this.code = error.code;
    this.message = error.message;
    this.descriptiveErrorMessage = getDescriptiveErrorMessage(error);
    this.errors = error.errors;
    this.remediation = error.remediation;
    const prefix = formatAsPrefix(error.message);
    // eslint-disable-next-line i18next/no-literal-string
    this.employeeMessage = this.remediation != null ? `${prefix}. ${this.remediation}` : this.message;
  }
}

export type ResponseErrorPackage = {
  responseType: "ERROR",
  error: ResponseErrorType,
};

export const ResponseInternalServerError: ResponseErrorType = {
  code: 500,
  message: `${window.gettext("Unknown Error")}`,
};

/**
 * I've only implemented enough of the 'Response' type for our purposes, as I can't find a good/complete type
 * definition for it. More properties may need to be added as we make greater use of this file.
 */
export type Response<SuccessType> = {
  headers: Headers,
  ok: boolean,
  redirected: boolean,
  status: number,
  statusText: string,
  url: string,

  readonly text: () => Promise<string>,
  readonly json: () => Promise<SuccessType | ResponseError>,
};

export class TimeoutError extends InternalOnlyError {}

function timeoutPromise<R>(ms: number, promise: Promise<R>): Promise<R> {
  return new Promise<R>((resolve, reject) => {
    setTimeout(() => {
      // eslint-disable-next-line i18next/no-literal-string
      reject(new TimeoutError(`Request took longer than ${ms}ms and timed out.`));
    }, ms);
    promise.then(resolve, reject);
  });
}

/**
 * This is effectively a middleware hook into receiving a handled server error, when our server sent us back the
 * expected error format.
 *
 * If our server knows what's going on and throws an exception on purpose, we'll end up with a success response with
 * an error dictionary describing the error the server noticed. This is the error handler we'll enter in that case.
 *
 * @param error
 */
export function handleErrorJSON(error: ResponseError): void {
  console.log(`[${error.code}] Error `, error); // eslint-disable-line no-console
}

// using jQuery - from Django docs: https://docs.djangoproject.com/en/2.0/ref/csrf/#ajax
export function getCookie(name: string): ?string {
  let cookieValue = null;
  if (document.cookie && document.cookie !== "") {
    const cookies = document.cookie.split(";");
    cookies.every((rawCookie) => {
      const cookie = rawCookie.trim();
      // Does this cookie string begin with the name we want?
      if (cookie.substring(0, name.length + 1) === `${name}=`) {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        return false; // Returning false breaks the loop.
      }
      return true;
    });
  }
  return cookieValue;
}

/* eslint-disable i18next/no-literal-string */
const malformedResponseError: ResponseErrorType = {
  code: 500,
  message: "Malformed Response",
};
/* eslint-disable i18next/no-literal-string */

const contentTypeHeaderIncludesApplicationJson = (headers: Headers) => {
  const contentType = headers.get("content-type");
  return contentType != null && includes(contentType, "application/json");
};

const isValidJSONResponse = (response: interface { readonly status: number, readonly headers: Headers }): boolean => {
  // We assume we got a bad response from the server (e.g. it returned HTML when we expected JSON) if the response is
  // 401 (in which case there's no response data), or if the response doesn't include the json content-type header.
  return response.status !== HTTP_401_UNAUTHORIZED || contentTypeHeaderIncludesApplicationJson(response.headers);
};

export function parseResponseData<APIResponse, Parsed>(
  response: ResponseDataSuccess<APIResponse>,
  parseFn: (APIResponse) => Parsed
): Parsed | ResponseError {
  if (response.responseType === "SUCCESS") {
    return parseFn(response.data);
  }
  return new ResponseError(malformedResponseError);
}

// rejected-promise value; body reads .message before Flow can narrow
// $FlowFixMe[unclear-type]
export function getErrorMessageFromRejectedPromise(error: any): string {
  if (
    // The native JS error object always contains a non-nullable message string.
    error instanceof Error ||
    // Sometimes we might have an object shaped like an error, with a message, if so, we can use that.
    // $FlowExpectedError[method-unbinding]
    (Object.prototype.hasOwnProperty.call(error, "message") && error.message instanceof String)
  ) {
    // Handle Firefox and Chrome's specific fetch errors that occur when they fail to connect to the target
    // server (eg. our server is down, the DNS lookup fails, etc).
    if (
      [
        "Failed to fetch", // Chrome
        "NetworkError when attempting to fetch resource.", // Firefox
        "Network request failed", // Safari and others that don't support fetch (from polyfill).
      ].includes(error.message)
    ) {
      return window.gettext("A network error occurred. Please try again later.");
    }
    return error.message;
  }
  if (error instanceof ResponseError) {
    return error.descriptiveErrorMessage;
  }
  // Sometimes we may have rejected a promise with a string instead of an error, if so, use that string.
  if (error instanceof String) {
    return error.toString();
  }
  // If we get an error that isn't an error, error-like object, or a string, give up and use a generic message.
  return window.gettext("An unknown error has occurred.");
}

/**
 * This makes use of the built-in browser 'fetch' method. It differs because it automatically fetches the 'csrftoken',
 * as formatted and stored by Django, and passes it along with the fetch request via HTTP Headers. Note that you can
 * override the automatically included headers, but you likely will never want to.
 *
 * For more info on fetch, see: https://developer.mozilla.org/en-US/docs/Web/API/Fetch_API
 *
 * @param requestURL The URL to 'fetch' from.
 * @param options The options to send with the request.
 * @returns {Promise<Response>}
 */
export default function fetchWithAuth<SuccessType>(
  requestURL: string,
  // fetch init options; permissive library boundary
  // $FlowFixMe[unclear-type]
  options: any = {}
): Promise<ResponseDataSuccess<SuccessType> | ResponseNoDataSuccess | ResponseErrorPackage> {
  const cookie = getCookie("csrftoken");
  // If we're testing, lets not worry about CSRF tokens, since the API call responses are mocked.
  if (cookie == null && process.env.NODE_ENV !== "test") {
    throw new InternalOnlyError("Retrieving CSRFToken failed.");
  }
  const { headers, ...rest } = options;
  return timeoutPromise<ResponseDataSuccess<SuccessType> | ResponseNoDataSuccess | ResponseErrorPackage>(
    FETCH_TIMEOUT_MS,
    new Promise<ResponseDataSuccess<SuccessType> | ResponseNoDataSuccess | ResponseErrorPackage>((resolve, reject) => {
      sentryTrackedFetch(requestURL, {
        headers: {
          Accept: "application/json",
          "X-CSRFToken": cookie,
          "Content-Type": "application/json",
          ...headers,
        },
        ...rest,
        credentials: "same-origin",
      })
        .then((response) => {
          // Validate we have a JSON response. If we don't, we resolve with responseType ERROR.
          if (!isValidJSONResponse(response)) {
            // Our server might return us 400 or 500 errors that aren't properly formatted JSON, but we can still build
            // a valid error, if we have the status code and status text.
            if ((response?.status || 0) > 399 && response?.statusText != null) {
              return resolve({
                responseType: "ERROR",
                error: {
                  code: response.status,
                  message: response.statusText,
                },
              });
            }
            // If we don't have a status or status text, use an unknown internal server error.
            return resolve({
              responseType: "ERROR",
              error: ResponseInternalServerError,
            });
          }

          // If the response is a 202 or 204 .json() will throw because the response includes no data.
          if ([HTTP_202_ACCEPTED, HTTP_204_NO_CONTENT].includes(response.status)) {
            return resolve({
              responseType: "SUCCESS_NO_DATA",
              statusCode: response.status,
            } as ResponseNoDataSuccess);
          }

          // Our JSON response should include either 'error' or 'data' as a root key. 'error' takes precedence.
          // This return format is assumed because of the HSMS API convention.
          return response
            .json()
            .then((responseJSON) => {
              if (responseJSON.error != null) {
                handleErrorJSON(responseJSON.error);
                return resolve({
                  responseType: "ERROR",
                  error: responseJSON.error,
                } as ResponseErrorPackage);
              }
              if (responseJSON.data != null) {
                return resolve({
                  responseType: "SUCCESS",
                  data: responseJSON.data,
                  statusCode: response.status,
                } as ResponseDataSuccess<SuccessType>);
              }
              return reject(malformedResponseError);
            })
            .catch((jsonError) => {
              logBreadcrumbToSentry(jsonError);
              return reject(malformedResponseError);
            });
        })
        .catch((error) => {
          // Basically we want to catch our 'inner' exception and reject it with the outer promise's reject.
          // I thought things worked this way by default, but this is required to get appropriate behaviour.
          reject(error);
        });
    })
  );
}

export function fetchWithAuthAndParseResponse<APIResponse, Parsed>(
  requestURL: string,
  parseFn: (APIResponse) => Parsed,
  // fetch init options; permissive library boundary
  // $FlowFixMe[unclear-type]
  options: any = {}
): Promise<Parsed | ResponseError> {
  return fetchWithAuth<APIResponse>(requestURL, options).then((response) => {
    if (response.responseType === "SUCCESS") {
      return parseResponseData(response, parseFn);
    }
    if (response.responseType === "ERROR") {
      return new ResponseError(response.error);
    }
    logBreadcrumbToSentry(response);
    throw new InternalOnlyError("We're in an unknown state. fetchWithAuth returned invalid data.");
  });
}

export function fetchWithNoExpectedResponseData(
  requestURL: string,
  // fetch init options; permissive library boundary
  // $FlowFixMe[unclear-type]
  options: any = {}
): Promise<ResponseNoDataSuccess | ResponseError> {
  return fetchWithAuth<unknown>(requestURL, options).then((response) => {
    if (response.responseType === "ERROR") {
      return new ResponseError(response.error);
    }
    return {
      responseType: "SUCCESS_NO_DATA",
      statusCode: response.statusCode,
    };
  });
}

function requestURLWithPageParam(rawURL: string, page: number) {
  const url = new URL(rawURL);
  url.searchParams.set("page", page.toString());
  return url.toString();
}

/**
 * This method is a helper for querying from an API where paginated results need to be handled. This function is meant
 * to mask the pagination and return all the results at once, for parsing. So, this can't be used to query 1 page,
 * display it, query another page, then display that, etc. This fetches all the data before returning a successful
 * promise response.
 *
 * NOTE: This method expects the API to return pagination data in the dynamic rest format we use. Eg.
 *  "meta":{
 *    "total_results":0,
 *    "per_page":1000,
 *    "total_pages":1,
 *    "page":1
 *  }
 * That said, currently only { "meta": { "total_pages": X } } is required, the rest of the values are unused.
 *
 * @param requestURL The request URL, this should not include page data, if it does it will be overwritten.
 * @param options Any additional needed options for the 'fetch'. Extra HTTP headers, for example.
 * @param getTotalPagesFn By default the number of pages are retrieved from the total_pages key in the response meta.
 * This function overrides that assumption.
 * @returns {Promise} If successful, a promise with an array of valid responses. Otherwise a promise rejection.
 */
export async function fetchAllPagesAndParseResponses<APIResponse>(
  requestURL: string,
  // fetch init options; permissive library boundary
  // $FlowFixMe[unclear-type]
  options: any = {},
  // dynamic pagination meta payload; callback param variance
  // $FlowFixMe[unclear-type]
  getTotalPagesFn?: (meta: any) => number
): Promise<Array<ResponseDataSuccess<APIResponse>>> {
  let totalPages = null;
  let allResponses = [];
  const pageOneRequestURL = requestURLWithPageParam(requestURL, 1);

  const response = await timeoutPromise(FETCH_TIMEOUT_MS, fetchWithAuth(pageOneRequestURL, options)).catch(
    logErrorBreadcrumbToSentryAndRethrow
  );

  // Flow can't understand that 'logErrorBreadcrumbToSentryAndRethrow' will never return (it rethrows) and thus this
  // is of type 'ResponseDataSuccess<APIResponse>' if the code doesn't crash.
  // rethrow-guarded value; bridged to typed response via cast
  // $FlowFixMe[unclear-type]
  const typeSafeResponse = response as any as ResponseDataSuccess<APIResponse>;

  if (typeSafeResponse.responseType === "SUCCESS") {
    allResponses.push(typeSafeResponse);
    if (getTotalPagesFn) {
      // $FlowExpectedError[incompatible-use]
      totalPages = getTotalPagesFn(typeSafeResponse.data.meta);
    } else {
      // $FlowExpectedError[incompatible-use]
      ({ total_pages: totalPages } = typeSafeResponse.data.meta);
    }

    if (totalPages > 1) {
      const allRequestURLs = range(2, totalPages + 1).map((page) => requestURLWithPageParam(requestURL, page));
      const remainingRequests = allRequestURLs.map((url) => fetchWithAuth<unknown>(url, options));
      const remainingResponses = await timeoutPromise(FETCH_TIMEOUT_MS, Promise.all(remainingRequests)).catch(
        logErrorBreadcrumbToSentryAndRethrow
      );
      // Flow can't understand that 'logErrorBreadcrumbToSentryAndRethrow' will never return (it rethrows) and thus
      // this is of type 'Array<ResponseDataSuccess<APIResponse>>' if the code doesn't crash.
      // rethrow-guarded value; bridged to typed array via cast
      // $FlowFixMe[unclear-type]
      const typeSafeRemainingResponses = remainingResponses as any as Array<ResponseDataSuccess<APIResponse>>;
      allResponses = [...allResponses, ...typeSafeRemainingResponses];
    }
  } else {
    throw new InternalOnlyError("Fetching first page failed.");
  }

  const successResponses: Array<ResponseDataSuccess<APIResponse>> = [];
  // response union shape not statically tracked here; branch on responseType at runtime
  // $FlowFixMe[unclear-type]
  allResponses.forEach((unknownResponse: any) => {
    if (unknownResponse.responseType === "SUCCESS") {
      successResponses.push(unknownResponse);
    } else if (unknownResponse.responseType === "ERROR") {
      throw new Error(unknownResponse.error);
    } else {
      throw new InternalOnlyError("We're in an unknown state. fetchWithAuth returned invalid data.");
    }
  });

  const successfullyParsedResponses = [];
  successResponses.forEach((successResponse) => {
    successfullyParsedResponses.push(successResponse);
  });

  return successfullyParsedResponses;
}
